import { findBehaviorDefinition } from '@erp/json-renderer/apps';
import type { BehaviorDefinition } from '@erp/json-renderer/apps';
import type { AppStateStore } from '@erp/json-renderer/apps/react';
import type {
  BehaviorOrigin,
  CompiledBehaviorAction,
  CompiledBehaviorInstance,
  CompiledBehaviorPlan,
  CompiledBehaviorPolicy,
} from '@erp/shared-types';
import type { AppActionHandler } from '@erp/json-renderer/apps/react';
import type { AppController, AppInvocationSession } from './appController';
import { AppActionError } from './appController';
import { APP_DIAGNOSTIC_LIMIT, createBoundedLog } from './appDiagnostics';
import type { BoundedLog } from './appDiagnostics';
import {
  behaviorStateTarget,
  isWithin,
  resolvePayload,
} from './appBehaviorPayload';
import {
  BEHAVIOR_RUNNERS,
  BOUND_WRITE_ROUTES,
  messageOf,
} from './appBehaviorOperations';
import type { BehaviorRunner, OperationScope } from './appBehaviorOperations';
import { summarize } from './appInspection';

/**
 * The synchronous behavior host: it owns `/$behaviors/<instance>` for every
 * compiled instance, routes a view's direct write to an editable key through
 * the operation that declares it, runs an action alias as its operation, and
 * executes explicit event policies after the source operation's state is
 * published.
 *
 * There is one store. Behavior state is published into it, never mirrored.
 */

export interface BehaviorTraceContext {
  sequence: number;
  at: number;
}

export type BehaviorTraceEvent =
  | {
      kind: 'operation-requested';
      operationId: number;
      parentOperationId: number | null;
      instance: string;
      operation: string;
      /** The alias the view used, or null for a write, policy or follow-up. */
      alias: string | null;
      origins: readonly BehaviorOrigin[];
      input: unknown;
      sessionId: number | null;
    }
  | {
      kind: 'operation-settled';
      operationId: number;
      instance: string;
      operation: string;
      /** The command invocation this operation waited on, when it ran one. */
      invocationId: number | null;
      changedPaths: readonly string[];
    }
  | {
      kind: 'operation-failed';
      operationId: number;
      instance: string;
      operation: string;
      reason: 'invalid' | 'busy' | 'failed' | 'canceled';
      message: string;
      /** The command invocation the operation waited on, when it reached one. */
      invocationId?: number | null;
    }
  | {
      kind: 'fact-emitted';
      /** The operation that emitted the fact; follow-ups record it as their parent. */
      operationId: number;
      instance: string;
      event: string;
      payload: unknown;
      targets: number;
    }
  | {
      kind: 'fact-stopped';
      operationId: number;
      instance: string;
      event: string;
    }
  | {
      kind: 'follow-up-failed';
      operationId: number;
      instance: string;
      operation: string;
      message: string;
    }
  | {
      kind: 'write-rejected';
      path: string;
      reason: 'owned-root' | 'owned-field' | 'invalid-value' | 'busy';
    };

export type BehaviorTraceEntry = BehaviorTraceEvent & BehaviorTraceContext;

export interface AppBehaviorHost {
  /** The App's one store, with behavior-owned writes routed or rejected. */
  readonly store: AppStateStore;
  /** Action aliases the view may trigger, keyed by the alias name. */
  readonly handlers: Record<string, AppActionHandler>;
  readonly diagnostics: BoundedLog<BehaviorTraceEntry>;
  inspect(eventLimit?: number): {
    instances: ReadonlyArray<{
      name: string;
      definition: string;
      version: number;
      sessionId: number | null;
      state: unknown;
    }>;
    events: readonly BehaviorTraceEntry[];
    limits: { events: number; valueSummaryCharacters: number };
  };
  /**
   * A mount's lease. A React StrictMode replay cleans up and sets up again, so
   * a new lease revives the host instead of leaving it permanently disposed.
   */
  connect(): () => void;
  dispose(): void;
}

export function createAppBehaviorHost(options: {
  plan: CompiledBehaviorPlan;
  controller: AppController;
  /** App state the server owns: feed keys, command status and `$data`. */
  ownedRoots: readonly string[];
  diagnosticLimit?: number;
}): AppBehaviorHost {
  const { plan, controller } = options;
  const diagnostics = createBoundedLog<BehaviorTraceEntry>(
    options.diagnosticLimit ?? APP_DIAGNOSTIC_LIMIT,
  );
  const definitions = new Map<string, BehaviorDefinition>();
  const instances = new Map<string, CompiledBehaviorInstance>();
  for (const instance of plan.instances) {
    const definition = findBehaviorDefinition(
      instance.definition,
      instance.version,
    );
    if (!definition) continue;
    definitions.set(instance.name, definition);
    instances.set(instance.name, instance);
  }
  const policies = new Map<string, CompiledBehaviorPolicy>(
    plan.policies.map((policy) => [
      `${policy.instance}.${policy.event}`,
      policy,
    ]),
  );
  const roots = [...options.ownedRoots, '/$behaviors', '/commands', '/$data'];
  const sessions = new Map<string, AppInvocationSession | null>();
  // Plain view writes recorded during the current gesture. A dispatch takes
  // them so a rejected action can undo its own optimistic intent, without
  // touching edits that arrive later, while it is still pending.
  let journal: Array<{ path: string; before: unknown }> = [];
  let journalFlush: (() => void) | null = null;
  let sequence = 0;
  let operationSequence = 0;
  let disposed = false;
  let lease = 0;

  seedBehaviorState();
  const store = wrapStore();
  const handlers = Object.fromEntries(
    Object.entries(plan.actions).map(([alias, action]) => [
      alias,
      (params: Record<string, unknown>) => dispatch(alias, action, params),
    ]),
  );

  function log(event: BehaviorTraceEvent): void {
    diagnostics.record({ ...event, sequence: ++sequence, at: Date.now() });
  }

  function seedBehaviorState(): void {
    if (plan.instances.length === 0) return;
    controller.publish(
      Object.fromEntries(
        plan.instances.map((instance) => [instance.statePath, instance.state]),
      ),
    );
  }

  // ── Store: route or reject a view write ───────────────────────────────────

  function wrapStore(): AppStateStore {
    return {
      ...controller.store,
      set(path, value) {
        writeEntries([{ path, value }]);
      },
      update(changes) {
        writeEntries(
          Object.entries(changes).map(([path, value]) => ({ path, value })),
        );
      },
    };
  }

  type WritePlan =
    | { kind: 'plain'; path: string; value: unknown }
    | {
        kind: 'routed';
        path: string;
        value: unknown;
        instance: CompiledBehaviorInstance;
        definition: BehaviorDefinition;
        operation: string;
        inputKey: string;
      }
    | {
        kind: 'rejected';
        path: string;
        reason: 'owned-root' | 'owned-field' | 'invalid-value' | 'busy';
      };

  function writeEntries(
    entries: ReadonlyArray<{ path: string; value: unknown }>,
  ): void {
    if (disposed) return;
    const plans = entries.map(classifyWrite);
    const rejected = plans.filter((plan) => plan.kind === 'rejected');
    if (rejected.length > 0) {
      for (const plan of rejected) {
        log({ kind: 'write-rejected', path: plan.path, reason: plan.reason });
        reconcilePath(plan.path);
      }
      return;
    }
    for (const plan of plans) applyWrite(plan);
  }

  function classifyWrite(entry: { path: string; value: unknown }): WritePlan {
    const target = behaviorStateTarget(entry.path);
    if (target) return classifyBehaviorWrite(entry, target);
    if (roots.some((root) => isWithin(entry.path, root)))
      return { kind: 'rejected', path: entry.path, reason: 'owned-root' };
    return { kind: 'plain', path: entry.path, value: entry.value };
  }

  function classifyBehaviorWrite(
    entry: { path: string; value: unknown },
    target: { instance: string; key: string },
  ): WritePlan {
    const instance = instances.get(target.instance);
    const definition = instance ? definitions.get(target.instance) : undefined;
    if (!instance || !definition)
      return { kind: 'rejected', path: entry.path, reason: 'owned-field' };
    const route = BOUND_WRITE_ROUTES[definition.id]?.[target.key];
    if (!route)
      return { kind: 'rejected', path: entry.path, reason: 'owned-field' };
    const parsed = definition.operations[route.operation]?.input.safeParse({
      [route.input]: entry.value,
    });
    if (!parsed?.success)
      return { kind: 'rejected', path: entry.path, reason: 'invalid-value' };
    return {
      kind: 'routed',
      path: entry.path,
      value: entry.value,
      instance,
      definition,
      operation: route.operation,
      inputKey: route.input,
    };
  }

  function applyWrite(plan: WritePlan): void {
    if (plan.kind === 'plain') {
      noteJournalWrite({
        path: plan.path,
        before: controller.store.get(plan.path),
      });
      controller.store.set(plan.path, plan.value);
      return;
    }
    if (plan.kind !== 'routed') return;
    try {
      const result = runOperation({
        instance: plan.instance,
        definition: plan.definition,
        operation: plan.operation,
        rawInput: { [plan.inputKey]: plan.value },
        parentOperationId: null,
        alias: null,
        origins: [],
      });
      if (isThenable(result)) void result.catch(() => undefined);
    } catch (error) {
      // The view wrote optimistically; the owner refused. Revert the control
      // rather than throwing into the renderer's DOM handler.
      log({
        kind: 'write-rejected',
        path: plan.path,
        reason: failureReason(error) === 'busy' ? 'busy' : 'invalid-value',
      });
      reconcilePath(plan.path);
    }
  }

  /**
   * Keep a write for this gesture. The gesture ends with the current task, but
   * the renderer resolves an action in a microtask, so the flush waits for the
   * next task: the action still sees its own writes, and a later edit does not.
   */
  function noteJournalWrite(entry: { path: string; before: unknown }): void {
    journal.push(entry);
    if (journal.length > 256) journal = [];
    if (journalFlush) return;
    journalFlush = () => {
      journalFlush = null;
      journal = [];
    };
    setTimeout(journalFlush, 0);
  }

  /** Re-publish a rejected path's current value so a controlled input reverts. */
  function reconcilePath(path: string): void {
    const current = controller.store.get(path);
    controller.publish({ [path]: current });
  }

  // ── Dispatch ──────────────────────────────────────────────────────────────

  function dispatch(
    alias: string,
    action: CompiledBehaviorAction,
    params: Record<string, unknown>,
  ): Promise<unknown> {
    const optimistic = journal;
    journal = [];
    const promise = runAlias(alias, action, params, optimistic);
    // The renderer may ignore a handler's Promise; report it in the trace
    // instead of leaving it unhandled.
    promise.catch(() => undefined);
    return promise;
  }

  async function runAlias(
    alias: string,
    action: CompiledBehaviorAction,
    params: Record<string, unknown>,
    optimistic: ReadonlyArray<{ path: string; before: unknown }>,
  ): Promise<unknown> {
    const instance = instances.get(action.instance);
    const definition = definitions.get(action.instance);
    if (!instance || !definition)
      throw new AppActionError({
        reason: 'invalid',
        message: `No behavior instance "${action.instance}".`,
      });
    try {
      const input = resolvePayload(action.payload, params);
      const result = await runOperation({
        instance,
        definition,
        operation: action.operation,
        rawInput: input,
        parentOperationId: null,
        alias,
        origins: action.origins,
      });
      return result;
    } catch (error) {
      rollbackWrites(optimistic);
      throw error;
    }
  }

  /** Undo one action's optimistic view writes, newest first. */
  function rollbackWrites(
    entries: ReadonlyArray<{ path: string; before: unknown }>,
  ): void {
    if (entries.length === 0) return;
    controller.publish(
      Object.fromEntries(
        [...entries].reverse().map((entry) => [entry.path, entry.before]),
      ),
    );
  }

  // ── Operations ────────────────────────────────────────────────────────────

  interface OperationArgs {
    readonly instance: CompiledBehaviorInstance;
    readonly definition: BehaviorDefinition;
    readonly operation: string;
    readonly rawInput: unknown;
    readonly parentOperationId: number | null;
    readonly alias: string | null;
    readonly origins: readonly BehaviorOrigin[];
  }

  function runOperation(args: OperationArgs): unknown {
    const operationId = ++operationSequence;
    const operation = args.definition.operations[args.operation];
    log({
      kind: 'operation-requested',
      operationId,
      parentOperationId: args.parentOperationId,
      instance: args.instance.name,
      operation: args.operation,
      alias: args.alias,
      origins: args.origins,
      input: summarize(args.rawInput, 600),
      sessionId: sessions.get(args.instance.name)?.id ?? null,
    });
    if (!operation) {
      const error = new AppActionError({
        reason: 'invalid',
        message: `No operation "${args.operation}" on "${args.definition.id}".`,
      });
      logFailure(operationId, args, error);
      throw error;
    }
    const parsed = operation.input.safeParse(args.rawInput);
    if (!parsed.success) {
      const error = new AppActionError({
        reason: 'invalid',
        message: `${args.definition.id}.${args.operation}: ${parsed.error.issues
          .map((issue) => `${issue.path.join('.') || 'input'} ${issue.message}`)
          .join('; ')}`,
      });
      logFailure(operationId, args, error);
      throw error;
    }
    const runner = BEHAVIOR_RUNNERS[args.definition.id]?.[args.operation];
    if (!runner) {
      const error = new AppActionError({
        reason: 'invalid',
        message: `"${args.definition.id}" has no implementation for "${args.operation}".`,
      });
      logFailure(operationId, args, error);
      throw error;
    }
    return executeOperation(runner, operationId, args, parsed.data);
  }

  function executeOperation(
    runner: BehaviorRunner,
    operationId: number,
    args: OperationArgs,
    input: unknown,
  ): unknown {
    const changed: string[] = [];
    const invocation = { id: null as number | null };
    const scope = createScope(args, operationId, changed, invocation);
    const settle = () =>
      log({
        kind: 'operation-settled',
        operationId,
        instance: args.instance.name,
        operation: args.operation,
        invocationId: invocation.id,
        changedPaths: changed,
      });
    try {
      const result = runner(scope, input);
      if (!isThenable(result)) {
        settle();
        return result;
      }
      return result.then(
        (value) => {
          settle();
          return value;
        },
        (error: unknown) => {
          logFailure(operationId, args, error, invocation.id);
          throw error;
        },
      );
    } catch (error) {
      logFailure(operationId, args, error, invocation.id);
      throw error;
    }
  }

  function logFailure(
    operationId: number,
    args: OperationArgs,
    error: unknown,
    invocationId: number | null = null,
  ): void {
    log({
      kind: 'operation-failed',
      operationId,
      instance: args.instance.name,
      operation: args.operation,
      reason: failureReason(error),
      message: messageOf(error),
      invocationId,
    });
  }

  function createScope(
    args: OperationArgs,
    operationId: number,
    changed: string[],
    invocation: { id: number | null },
  ): OperationScope {
    const { instance } = args;
    const keyPath = (key: string) => `${instance.statePath}/${key}`;
    return {
      instance,
      operationId,
      parentOperationId: args.parentOperationId,
      read(key) {
        return controller.store.get(keyPath(key));
      },
      write(changes) {
        const patch: Record<string, unknown> = {};
        for (const [key, value] of Object.entries(changes)) {
          patch[keyPath(key)] = value;
          changed.push(keyPath(key));
        }
        controller.publish(patch);
      },
      emit(event, payload) {
        emitFact(instance, event, payload, operationId);
      },
      command(slot, params) {
        return runCommand(instance, slot, params, invocation);
      },
      openSession() {
        const session = controller.openSession();
        sessions.set(instance.name, session);
        return session;
      },
      retireSession() {
        sessions.get(instance.name)?.retire();
        sessions.set(instance.name, null);
      },
      sessionId() {
        return sessions.get(instance.name)?.id ?? null;
      },
    };
  }

  function runCommand(
    instance: CompiledBehaviorInstance,
    slot: string,
    params: Record<string, unknown>,
    invocation: { id: number | null },
  ): Promise<unknown> {
    const binding = instance.services[slot];
    if (!binding)
      return Promise.reject(
        new AppActionError({
          reason: 'invalid',
          message: `No command is bound to the "${slot}" slot.`,
        }),
      );
    return controller.request(binding.command, params, {
      sessionId: sessions.get(instance.name)?.id,
      onInvocation: (invocationId) => {
        invocation.id = invocationId;
      },
    });
  }

  // ── Events and policies ───────────────────────────────────────────────────

  function emitFact(
    instance: CompiledBehaviorInstance,
    event: string,
    payload: unknown,
    parentOperationId: number,
  ): void {
    const policy = policies.get(`${instance.name}.${event}`);
    if (!policy || policy.stop) {
      log({
        kind: 'fact-stopped',
        operationId: parentOperationId,
        instance: instance.name,
        event,
      });
      return;
    }
    log({
      kind: 'fact-emitted',
      operationId: parentOperationId,
      instance: instance.name,
      event,
      payload: summarize(payload, 600),
      targets: policy.targets.length,
    });
    for (const target of policy.targets) {
      runFollowUp(instance, target, payload, parentOperationId);
    }
  }

  function runFollowUp(
    source: CompiledBehaviorInstance,
    target: CompiledBehaviorPolicy['targets'][number],
    payload: unknown,
    parentOperationId: number,
  ): void {
    const instance = instances.get(target.instance);
    const definition = definitions.get(target.instance);
    if (!instance || !definition) return;
    try {
      const input = resolvePayload(target.payload, payload);
      const result = runOperation({
        instance,
        definition,
        operation: target.operation,
        rawInput: input,
        parentOperationId,
        alias: null,
        origins: [],
      });
      if (isThenable(result)) {
        void result.catch((error: unknown) =>
          logFollowUpFailure(source, target, parentOperationId, error),
        );
      }
    } catch (error) {
      logFollowUpFailure(source, target, parentOperationId, error);
    }
  }

  function logFollowUpFailure(
    source: CompiledBehaviorInstance,
    target: CompiledBehaviorPolicy['targets'][number],
    parentOperationId: number,
    error: unknown,
  ): void {
    log({
      kind: 'follow-up-failed',
      operationId: parentOperationId,
      instance: target.instance,
      operation: target.operation,
      message: `${source.name}.${target.operation}: ${messageOf(error)}`,
    });
  }

  // ── Inspection ────────────────────────────────────────────────────────────

  return {
    store,
    handlers,
    diagnostics,
    inspect(eventLimit = 20) {
      const snapshot = diagnostics.getSnapshot();
      return {
        instances: plan.instances.map((instance) => ({
          name: instance.name,
          definition: instance.definition,
          version: instance.version,
          sessionId: sessions.get(instance.name)?.id ?? null,
          state: summarize(readInstanceState(instance), 1200),
        })),
        events: snapshot.events
          .slice(-Math.max(1, Math.min(APP_DIAGNOSTIC_LIMIT, eventLimit)))
          .map((event) => ({ ...event })),
        limits: { events: APP_DIAGNOSTIC_LIMIT, valueSummaryCharacters: 1200 },
      };
    },
    connect() {
      disposed = false;
      const current = ++lease;
      return () => {
        if (lease !== current) return;
        disposeNow();
      };
    },
    dispose() {
      lease += 1;
      disposeNow();
    },
  };

  function disposeNow(): void {
    disposed = true;
    for (const session of sessions.values()) session?.retire();
    sessions.clear();
    journal = [];
  }

  function readInstanceState(
    instance: CompiledBehaviorInstance,
  ): Record<string, unknown> {
    const state = controller.store.get(instance.statePath);
    return state && typeof state === 'object'
      ? Object.fromEntries(Object.entries(state))
      : {};
  }
}

function failureReason(
  error: unknown,
): 'invalid' | 'busy' | 'failed' | 'canceled' {
  const reason = error instanceof AppActionError ? error.reason : 'failed';
  if (reason === 'invalid' || reason === 'busy' || reason === 'canceled')
    return reason;
  return 'failed';
}

function isThenable(value: unknown): value is Promise<unknown> {
  return value instanceof Promise;
}
